import React, { useEffect, useState } from 'react';
import * as DOMPurify from 'dompurify';
import { withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { Message, Icon, Button, Menu } from 'semantic-ui-react';

import * as actions from '@/store/actions';
import { RootState } from 'MyTypes';
import { ArticlePrice, ListState, ServicePriceMapping } from '@/types';
import { connect } from 'react-redux';
import {
    useCheckEaccountingAuthQuery,
    useGetEaccountingPriceMappingsQuery,
    useGetEaccountingPricesQuery,
} from '@/services/bmApi';
import PageHeader from '@/components/PageHeader';
import { getCompanyId } from '@/store/reducers/newAuth';
import PriceArticleMappingsTable from './PriceArticleMappingsTable';
import { ApplicationDispatch } from '@/store';
import RTKLoader from '@/components/RTKLoader';
import { UpdatedPrices } from '@/store/reducers/eaccounting';
import { Box } from 'grid-styled';
import { Link, Prompt } from 'react-router-dom';
import { resetUpdatedPrices } from '@/store/actions';
import ArticleMappingsTab from './ArticleMappingsTab';

type MappingTab = 'servicePrices' | 'articles';

interface Props extends LocalizeContextProps {
    preventRequiredActions?: string[];
    prices: ListState<{ Results: ArticlePrice[] }>;
    priceMappings: ListState<ServicePriceMapping>;
    updatedPrices: UpdatedPrices;
    companyId: string;
    dispatch: ApplicationDispatch;
}

const ArticlesLoader: React.FC<Props> = (props) => {
    const { companyId, updatedPrices, dispatch, translate } = props;

    const prices = useGetEaccountingPricesQuery({ Active: true, IncludePricesFromOtherDays: true });
    const priceMappings = useGetEaccountingPriceMappingsQuery({ CompanyId: companyId });
    const { isSuccess: authenticated } = useCheckEaccountingAuthQuery();

    const [tab, setTab] = useState<MappingTab>('servicePrices');

    useEffect(() => {
        return () => {
            if(updatedPrices.changed) {
                dispatch(resetUpdatedPrices());
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedPrices.changed])

    return (
        <>
            <PageHeader
                breadcrumbs={[
                    {
                        to: '/eaccounting/service-price-mappings',
                        content: translate('priceArticleMappings.heading'),
                    },
                ]}
            />
            {authenticated ? (
                <>
                <Menu secondary>
                    <Menu.Item
                        name="servicePrices"
                        data-cy="tab-service-price-mappings"
                        content={translate('eaccounting.mappings.servicePricesTab') as string}
                        active={tab === 'servicePrices'}
                        onClick={() => setTab('servicePrices')}
                    />
                    <Menu.Item
                        name="articles"
                        data-cy="tab-article-mappings"
                        content={translate('eaccounting.mappings.articlesTab') as string}
                        active={tab === 'articles'}
                        onClick={() => setTab('articles')}
                    />
                </Menu>
                {/* Both tabs stay mounted so unsaved edits survive switching between them. */}
                <div style={{ display: tab === 'servicePrices' ? undefined : 'none' }}>
                <Message icon info>
                    <Icon name="info" />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(translate('eaccounting.priceArticleMappings.messageText') as string),
                        }}
                    />
                </Message>
                <RTKLoader queries={[prices, priceMappings]}>
                    <PriceArticleMappingsTable
                        prices={prices.data!}
                        priceMappings={priceMappings.data!}
                        updatedPrices={updatedPrices}
                        onRemoveMapping={(id) => {
                            dispatch(
                                // @ts-ignore
                                actions.removePriceArticleMapping(id)
                            );
                        }}
                        onUpdateMapping={(value) => {
                            dispatch({
                                type: actions.UPDATE_EACCOUNTING_PRICE_EXTERNAL_REF,
                                payload: value,
                            });
                        }}
                        renderSaveButton={() => {
                            return (
                                <div className="clearfix">
                                    <Button
                                        data-cy="submit-price-article-mapping"
                                        onClick={() =>
                                            dispatch(
                                                actions.POST_EACCOUNTING_PRICES.request(
                                                    updatedPrices.entities
                                                )
                                            )
                                        }
                                        disabled={
                                            !updatedPrices.changed ||
                                            priceMappings.isFetching
                                        }
                                        loading={
                                            !updatedPrices.changed && priceMappings.isFetching
                                        }
                                        floated="right"
                                        primary
                                    >
                                        {translate('form.save')}
                                    </Button>
                                </div>
                            );
                        }}
                    />
                </RTKLoader>
                </div>
                <div style={{ display: tab === 'articles' ? undefined : 'none' }}>
                    <ArticleMappingsTab />
                </div>
                </>
            ) : (
                <Message icon info>
                    <Icon name="info" />
                    <Box as="span" mr="1">
                        {translate('eaccounting.priceArticleMappings.authenticateInThe')}
                    </Box>
                    <Link to="/eaccounting/settings">{translate('eaccounting.priceArticleMappings.settingsLink')}</Link>
                </Message>
            )}
            <Prompt
                when={updatedPrices.changed}
                // @ts-ignore
                message={translate('form.unsavedChangesMessage')}
            />
            <Prompt
                when={!updatedPrices.changed && priceMappings.isFetching}
                // @ts-ignore
                message={translate('form.saveInProgress.alertPrompt')}
            />
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        companyId: getCompanyId(state),
        updatedPrices: state.eaccounting.updatePrices,
    };
};

export default connect(mapStateToProps)(withLocalize(ArticlesLoader));
